import { firstValueFrom } from "rxjs"
import { initializeApp } from "firebase/app"
import { getMessaging, onMessage, getToken } from "firebase/messaging"
import { HttpService } from "@callcenter/services"

import { getFirebaseConfig } from "./firebase.config"

const getDeviceOS = (): string => {
  const ua = navigator.userAgent

  if (/android/i.test(ua)) {
    return "android"
  }

  return "ios"
}

const requestNotificationPermission = async (): Promise<boolean> => {
  if (!("Notification" in window)) {
    return false
  }

  try {
    const permission = await Notification.requestPermission()

    return permission === "granted"
  } catch {
    return false
  }
}

const getFCMToken = async () => {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") {
      return null
    }

    const app = initializeApp(getFirebaseConfig())
    const messaging = getMessaging(app)

    const scope = "/_front/"
    const swPath = `${scope}webapp-sw.js`

    const registration = await navigator.serviceWorker.register(swPath, { scope })

    if (!registration.active) {
      await new Promise<void>((resolve) => {
        const sw = registration.installing || registration.waiting

        if (!sw) {
          resolve()

          return
        }

        sw.addEventListener("statechange", () => {
          if (sw.state === "activated") {
            resolve()
          }
        })
      })
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    if (csrfToken && registration.active) {
      registration.active.postMessage({
        type: "SET_CSRF_TOKEN",
        csrfToken,
        os: getDeviceOS()
      })
    }

    const tokenOptions: { serviceWorkerRegistration: ServiceWorkerRegistration } = {
      serviceWorkerRegistration: registration
    }

    const token = await getToken(messaging, tokenOptions)

    return token
  } catch {
    return null
  }
}

const sendTokenToServer = async (token: string): Promise<void> => {
  try {
    const userId = window.user?.id

    if (!userId) {
      return
    }

    await firstValueFrom(
      HttpService.post(`/push_notification/users/${userId}/device_tokens`, {
        token,
        os: getDeviceOS(),
        provider: "fcm"
      })
    )
  } catch {
    // silent
  }
}

const initFirebaseService = async () => {
  const app = initializeApp(getFirebaseConfig())
  const messaging = getMessaging(app)

  onMessage(messaging, (payload) => {
    const data = payload.data || {}
    const pushType = data.type

    if (pushType === "wakeup" && data.call_id) {
      window.dispatchEvent(new CustomEvent("firebase-call-wakeup", { detail: data }))
    }

    if (pushType === "cancel" && data.call_id) {
      window.dispatchEvent(new CustomEvent("firebase-call-cancel", { detail: data }))
    }
  })
}

const getFirebaseWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    return false
  }

  const registrations = await navigator.serviceWorker.getRegistrations()
  const swRegistration = registrations.find((r) => r.active?.scriptURL.includes("webapp-sw"))

  return swRegistration
}

const deleteSWRegistration = async () => {
  try {
    const swRegistration = await getFirebaseWorker()

    if (!swRegistration) {
      return false
    }

    return await swRegistration.unregister()
  } catch {
    return false
  }
}

export const firebaseService = {
  initFirebaseService,
  getFirebaseWorker,
  deleteSWRegistration,
  requestNotificationPermission,
  sendTokenToServer,
  getFCMToken
}
